import { ICON_TYPE } from "@/components/library/Icon";
import { LpIntegrationsBlock, LpOneGailBlock } from "@/puck/landingBlocks";
import type { LandingComponents } from "@/puck/blocks/blockTypes";
import {
  ICON_TYPE_OPTIONS,
  SECTION_BACKGROUND_OPTIONS,
} from "@/puck/blocks/fieldOptions";

export const oneGailConfigs: Pick<
  LandingComponents,
  "LpOneGail" | "LpIntegrations"
> = {
  LpOneGail: {
    label: "One Gail (channels)",
    fields: {
      eyebrow: { type: "text" },
      titleLead: { type: "text", contentEditable: true },
      titleAccent: { type: "text", contentEditable: true },
      subtitle: { type: "textarea", contentEditable: true },
      background: {
        type: "select",
        options: [
          { label: "Transparent", value: "transparent" },
          { label: "Voice card", value: "voiceCard" },
        ],
      },
      channels: {
        type: "array",
        getItemSummary: (item) => item.title,
        defaultItemProps: {
          iconName: "phone",
          iconType: ICON_TYPE.LIGHT,
          accent: "primary",
          wide: false,
          title: "Voice",
          description: "Channel description.",
        },
        arrayFields: {
          iconName: { type: "text", label: "Icon name" },
          iconType: { type: "select", label: "Icon style", options: ICON_TYPE_OPTIONS },
          accent: {
            type: "select",
            options: [
              { label: "Primary", value: "primary" },
              { label: "WhatsApp", value: "whatsapp" },
            ],
          },
          wide: {
            type: "radio",
            options: [
              { label: "Normal", value: false },
              { label: "Wide", value: true },
            ],
          },
          title: { type: "text", contentEditable: true },
          description: { type: "textarea", contentEditable: true },
        },
      },
      languagesEyebrow: { type: "text" },
      languagesTitle: { type: "text", contentEditable: true },
      languagesDescription: { type: "textarea", contentEditable: true },
      languages: {
        type: "array",
        getItemSummary: (item) => item.name,
        defaultItemProps: { name: "English", code: "EN" },
        arrayFields: {
          name: { type: "text", contentEditable: true },
          code: { type: "text" },
        },
      },
      personalityEyebrow: { type: "text" },
      personalityTitle: { type: "text", contentEditable: true },
      personalities: {
        type: "array",
        getItemSummary: (item) => item.name,
        defaultItemProps: {
          initial: "G",
          name: "Gail",
          traits: "brand-neutral",
          avatar: "primary",
        },
        arrayFields: {
          initial: { type: "text" },
          name: { type: "text", contentEditable: true },
          traits: { type: "text", contentEditable: true },
          avatar: {
            type: "select",
            options: [
              { label: "Primary", value: "primary" },
              { label: "Navy", value: "navy" },
            ],
          },
        },
      },
    },
    defaultProps: {
      eyebrow: "One Gail",
      titleLead: "Every channel. Every language.",
      titleAccent: "Every personality.",
      subtitle: "One voice and one shift becomes a trillion, around the clock.",
      background: "transparent",
      channels: [
        { iconName: "phone", iconType: ICON_TYPE.LIGHT, accent: "primary", wide: false, title: "Voice", description: "Sub-300ms turn-taking." },
        { iconName: "message-lines", iconType: ICON_TYPE.LIGHT, accent: "primary", wide: false, title: "SMS", description: "TCPA-compliant. A2P 10DLC." },
        { iconName: "envelope", iconType: ICON_TYPE.LIGHT, accent: "primary", wide: false, title: "Email", description: "Threads natively." },
        { iconName: "comment-dots", iconType: ICON_TYPE.LIGHT, accent: "primary", wide: false, title: "Webchat", description: "Embedded in your portal." },
        { iconName: "whatsapp", iconType: ICON_TYPE.BRAND, accent: "whatsapp", wide: true, title: "WhatsApp Business", description: "Same Gail, same compliance." },
      ],
      languagesEyebrow: "Native fluency",
      languagesTitle: "Dozens of languages.",
      languagesDescription: "Switches mid-conversation when the customer does.",
      languages: [
        { name: "English", code: "EN" },
        { name: "Español", code: "ES" },
        { name: "Português", code: "PT" },
        { name: "Français", code: "FR" },
      ],
      personalityEyebrow: "Configurable personality",
      personalityTitle: "Per campaign, creditor, queue, or stage.",
      personalities: [
        { initial: "M", name: "Marisol", traits: "warm · Spanish", avatar: "primary" },
        { initial: "T", name: "Theo", traits: "firm · late-stage", avatar: "primary" },
        { initial: "G", name: "Gail", traits: "brand-neutral", avatar: "navy" },
      ],
    },
    render: LpOneGailBlock,
  },
  LpIntegrations: {
    label: "Integrations",
    fields: {
      eyebrow: { type: "text" },
      titleLead: { type: "text", contentEditable: true },
      titleAccent: { type: "text", contentEditable: true },
      subtitle: { type: "textarea", contentEditable: true },
      featuredBadge: { type: "text" },
      background: { type: "select", options: SECTION_BACKGROUND_OPTIONS },
      logoSet: {
        type: "select",
        label: "Logo set",
        options: [
          { label: "Collections", value: "collections" },
          { label: "Insurance", value: "insurance" },
        ],
      },
      items: {
        type: "array",
        getItemSummary: (item) => item.name,
        defaultItemProps: {
          name: "Integration",
          category: "Category",
          featured: false,
          tags: [],
        },
        arrayFields: {
          name: { type: "text", contentEditable: true },
          category: { type: "text", contentEditable: true },
          featured: {
            type: "radio",
            options: [
              { label: "No", value: false },
              { label: "Featured", value: true },
            ],
          },
          tags: {
            type: "array",
            getItemSummary: (item) => item.text,
            defaultItemProps: { text: "Tag" },
            arrayFields: { text: { type: "text" } },
          },
        },
      },
    },
    defaultProps: {
      eyebrow: "Integrations",
      titleLead: "Connects to the systems your",
      titleAccent: "team actually uses.",
      subtitle: "Native, supported, read–write.",
      featuredBadge: "Read · Write",
      background: "voiceCard",
      logoSet: "collections",
      items: [
        { name: "Finvi", category: "Collections suite", featured: true, tags: [{ text: "Artiva ADM" }, { text: "RevQ" }] },
        { name: "Salesforce", category: "CRM", featured: false, tags: [] },
        { name: "QuickBooks", category: "AR ledger", featured: false, tags: [] },
      ],
    },
    render: LpIntegrationsBlock,
  },
};
